/**
 * Review Form — posts a review to strausshouses/v1/reviews.
 *
 * The nonce and the "form rendered at" timestamp come from the API on load
 * rather than printed into the markup, so the page holding this form stays
 * cacheable and the timestamp is the reader's own arrival rather than the moment
 * the HTML was cached. The endpoint never publishes: a submission becomes a
 * pending review for a person to read.
 */
(function () {
	'use strict';

	var PHONE = '(920) 285-0877';

	function init(form) {
		if (!window.fetch || form.getAttribute('data-bound')) {
			return;
		}

		form.setAttribute('data-bound', '1');

		var endpoint = form.getAttribute('data-endpoint');
		var nonceEndpoint = form.getAttribute('data-nonce-endpoint');
		var button = form.querySelector('[type="submit"]');
		var status = form.querySelector('.review-form-status');
		var label = form.getAttribute('data-label') || 'Send';
		var sending = form.getAttribute('data-sending') || 'Sending…';
		var success = form.getAttribute('data-success') || 'Thank you.';

		if (!endpoint || !nonceEndpoint || !button || !status) {
			return;
		}

		// Which stars are lit. CSS cannot reach back to preceding siblings, so the
		// chosen value lives on the group and the hovered or focused one overrides
		// it while it is there. The radios stay the control — this is only paint.
		var group = form.querySelector('.review-form-stars');

		if (group) {
			var paint = function () {
				var checked = form.querySelector('[name="rating"]:checked');
				group.setAttribute('data-rating', checked ? checked.value : '');
			};

			var preview = function (el) {
				var input = el && el.htmlFor ? document.getElementById(el.htmlFor) : null;

				if (input) {
					group.setAttribute('data-preview', input.value);
				}
			};

			var clearPreview = function () {
				group.removeAttribute('data-preview');
			};

			group.addEventListener('change', paint);
			group.addEventListener('mouseleave', clearPreview);
			group.addEventListener('focusout', clearPreview);

			group.addEventListener('mouseover', function (e) {
				var label = e.target.closest ? e.target.closest('.review-form-star') : null;

				if (label) {
					preview(label);
				}
			});

			group.addEventListener('focusin', function (e) {
				if (e.target && e.target.name === 'rating') {
					var label = group.querySelector('label[for="' + e.target.id + '"]');
					preview(label);
				}
			});

			paint();
		}

		var ticket = null;

		function getTicket(force) {
			if (ticket && !force) {
				return Promise.resolve(ticket);
			}

			return fetch(nonceEndpoint, { headers: { Accept: 'application/json' } })
				.then(function (r) {
					return r.ok ? r.json() : null;
				})
				.then(function (d) {
					if (d && d.nonce) {
						ticket = d;
					}
					return ticket;
				})
				.catch(function () {
					return null;
				});
		}

		// Fetched on load, so the timestamp is when this reader arrived.
		getTicket(false);

		function say(message, isError) {
			status.textContent = message;
			status.classList.toggle('review-form-status-error', !!isError);
		}

		function field(name) {
			return form.querySelector('[name="' + name + '"]');
		}

		function value(name) {
			var el = field(name);
			return el ? el.value : '';
		}

		function busy(on) {
			button.disabled = on;
			button.textContent = on ? sending : label;
		}

		form.addEventListener('submit', function (e) {
			e.preventDefault();

			var quote = value('quote').trim();
			var name = value('name').trim();

			// Said here rather than after a round trip.
			if (quote.length < 20) {
				say('A sentence or two, please — the town and what the job was is plenty.', true);
				field('quote').focus();
				return;
			}

			if (!name) {
				say('A name we can put to the review, please.', true);
				field('name').focus();
				return;
			}

			busy(true);
			say(sending, false);

			getTicket(false).then(function (t) {
				if (!t) {
					busy(false);
					say('We could not reach the server. Please call us instead on ' + PHONE + '.', true);
					return null;
				}

				var checked = form.querySelector('[name="rating"]:checked');

				var payload = {
					quote: quote,
					name: name,
					email: value('email').trim(),
					rating: checked ? parseInt(checked.value, 10) : 5,
					town: value('town'),
					company: value('company'),
					rendered: t.rendered
				};

				return fetch(endpoint, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-WP-Nonce': t.nonce,
						Accept: 'application/json'
					},
					body: JSON.stringify(payload)
				}).then(function (r) {
					return r.json().then(function (d) {
						return { ok: r.ok, status: r.status, data: d };
					}, function () {
						return { ok: r.ok, status: r.status, data: null };
					});
				}).then(function (res) {
					busy(false);

					if (res.ok) {
						// Replace the form with the thank-you so it cannot be sent twice.
						var done = document.createElement('p');
						done.className = 'review-form-done lead mb-0';
						done.setAttribute('role', 'status');
						done.setAttribute('tabindex', '-1');
						done.textContent = (res.data && res.data.message) || success;
						form.parentNode.replaceChild(done, form);
						done.focus();
						return null;
					}

					// A stale nonce is worth one fresh ticket and a second try.
					if (403 === res.status) {
						return getTicket(true).then(function (fresh) {
							say(fresh
								? 'Please send that again — the form has refreshed itself.'
								: 'We could not reach the server. Please call us instead on ' + PHONE + '.', true);
							return null;
						});
					}

					say((res.data && res.data.message) || 'That did not send. Please call us instead on ' + PHONE + '.', true);
					return null;
				});
			}).catch(function () {
				busy(false);
				say('That did not send. Please call us instead on ' + PHONE + '.', true);
			});
		});
	}

	function initAll(root) {
		var forms = (root || document).querySelectorAll('.review-form-form');

		for (var i = 0; i < forms.length; i++) {
			init(forms[i]);
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () {
			initAll(document);
		});
	} else {
		initAll(document);
	}

	if (window.acf) {
		window.acf.addAction('render_block_preview/type=review-form', function ($block) {
			if ($block && $block[0] && $block[0].querySelector('.review-form-form')) {
				setTimeout(function () {
					initAll($block[0]);
				}, 200);
			}
		});
	}
})();
